import React, {useState} from 'react';
import {useServiceProviderControl} from '../hooks/useServiceProviderControl';
import Button from '../../../Presentation/components/ui/Button';
import Card from '../../../Presentation/components/ui/Card';
import {AppText, AppSectionTitle, AppBodyText} from '../../../Presentation/components/ui/Typography';
import {Brand} from '../models/serviceProvider.model';

interface UnassignBrandFormProps {
  providerId: number;
  providerName: string;
  assignedBrands: Brand[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function UnassignBrandForm ({
  providerId,
  providerName,
  assignedBrands,
  onSuccess,
  onCancel
}: UnassignBrandFormProps) {
  const {unassignBrand} = useServiceProviderControl();
  const [loading, setLoading] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedBrandId) {
      newErrors.brand = 'يجب اختيار براند';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await unassignBrand(providerId, selectedBrandId as number);
      onSuccess?.();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = (value: string) => {
    setSelectedBrandId(value ? parseInt(value) : '');

    // Clear error when user makes a selection
    if (errors.brand) {
      setErrors(prev => ({
        ...prev,
        brand: ''
      }));
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="p-6">
        <div className="mb-6">
          <AppSectionTitle className="font-bold text-gray-900">
            إلغاء ربط براند من مزود الخدمة
          </AppSectionTitle>
          <AppBodyText className="text-gray-600 mt-1">
            اختر البراند الذي تريد إلغاء ربطه من {providerName}
          </AppBodyText>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* البراندات المرتبطة */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اختر البراند لإلغاء الربط *
            </label>
            <select
              value={selectedBrandId}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary/30 focus:border-brand-500"
            >
              <option value="">
                اختر براند...
              </option>
              {assignedBrands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
            {errors.brand && (
              <p className="mt-1 text-sm text-red-600">{errors.brand}</p>
            )}
          </div>

          {/* رسالة إذا لم تكن هناك براندات مرتبطة */}
          {assignedBrands.length === 0 && (
            <div className="text-center py-6">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                لا توجد براندات مرتبطة
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                لم يتم ربط أي براندات بهذا مزود الخدمة
              </p>
            </div>
          )}

          {/* أزرار الإجراءات */}
          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              disabled={loading || assignedBrands.length === 0}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
            >
              {loading ? 'جاري الإلغاء...' : 'إلغاء ربط البراند'}
            </Button>
            {onCancel && (
              <Button
                type="button"
                onClick={onCancel}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md"
              >
                إلغاء
              </Button>
            )}
          </div>
        </form>

        {/* Guidelines */}
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-semibold text-red-900 mb-2">تحذير:</h4>
          <ul className="text-xs text-red-800 space-y-1">
            <li>• إلغاء ربط البراند سيؤثر على جميع العمليات المرتبطة به</li>
            <li>• يمكن إعادة ربط البراند لاحقاً إذا لزم الأمر</li>
            <li>• تأكد من عدم وجود عمليات نشطة للبراند قبل الإلغاء</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}
